// Timezone Management & Formatting Service for Lightweight Charts & App
const STORAGE_KEY = "trading_timezone";

export const TIMEZONE_OPTIONS = [
  {
    id: "local",
    label: "Giờ Máy Tính (Mặc Định)",
    desc: "Tự động đồng bộ theo đồng hồ máy tính / thiết bị của bạn",
    flag: "💻",
    iana: null,
  },
  {
    id: "Asia/Ho_Chi_Minh",
    label: "Việt Nam / Bangkok / Jakarta",
    desc: "Múi giờ Đông Dương (UTC+7)",
    flag: "🇻🇳",
    iana: "Asia/Ho_Chi_Minh",
  },
  {
    id: "Asia/Singapore",
    label: "Singapore / Thượng Hải / Hong Kong",
    desc: "Múi giờ Châu Á (UTC+8)",
    flag: "🇸🇬",
    iana: "Asia/Singapore",
  },
  {
    id: "Asia/Tokyo",
    label: "Tokyo / Seoul",
    desc: "Múi giờ Nhật Bản / Hàn Quốc (UTC+9)",
    flag: "🇯🇵",
    iana: "Asia/Tokyo",
  },
  {
    id: "Europe/London",
    label: "Luân Đôn / Dublin",
    desc: "Giờ Tây Âu GMT / BST (UTC+0 / UTC+1)",
    flag: "🇬🇧",
    iana: "Europe/London",
  },
  {
    id: "Europe/Berlin",
    label: "Frankfurt / Paris / Berlin",
    desc: "Giờ Trung Âu CET / CEST (UTC+1 / UTC+2)",
    flag: "🇩🇪",
    iana: "Europe/Berlin",
  },
  {
    id: "Europe/Athens",
    label: "Server Sàn MT5 / Exness (EET / EEST)",
    desc: "Giờ máy chủ sàn MetaTrader 5 (UTC+2 / UTC+3)",
    flag: "🏛️",
    iana: "Europe/Athens",
  },
  {
    id: "America/New_York",
    label: "New York / Wall Street",
    desc: "Giờ Thị trường Mỹ EST / EDT (UTC-5 / UTC-4)",
    flag: "🇺🇸",
    iana: "America/New_York",
  },
  {
    id: "America/Chicago",
    label: "Chicago / Sàn CME",
    desc: "Giờ Trung tâm Mỹ CST / CDT (UTC-6 / UTC-5)",
    flag: "🇺🇸",
    iana: "America/Chicago",
  },
  {
    id: "America/Los_Angeles",
    label: "Los Angeles / Bờ Tây Mỹ",
    desc: "Giờ Thái Bình Dương PST / PDT (UTC-8 / UTC-7)",
    flag: "🇺🇸",
    iana: "America/Los_Angeles",
  },
  {
    id: "UTC",
    label: "UTC (Giờ Chuẩn Quốc Tế)",
    desc: "Giờ chuẩn quốc tế không đổi (UTC+0)",
    flag: "🌐",
    iana: "UTC",
  },
];

export function getSavedTimezone() {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved && (saved === "local" || TIMEZONE_OPTIONS.some((t) => t.id === saved))) {
      return saved;
    }
  } catch (e) {}
  return "local";
}

export function saveTimezone(tzId) {
  try {
    localStorage.setItem(STORAGE_KEY, tzId);
  } catch (e) {}
}

export function getSystemTimezoneInfo() {
  try {
    const iana = Intl.DateTimeFormat().resolvedOptions().timeZone || "Asia/Ho_Chi_Minh";
    const offsetMin = -new Date().getTimezoneOffset();
    const sign = offsetMin >= 0 ? "+" : "-";
    const hours = Math.floor(Math.abs(offsetMin) / 60);
    const mins = Math.abs(offsetMin) % 60;
    const offsetStr = `UTC${sign}${hours}${mins > 0 ? `:${mins < 10 ? "0" : ""}${mins}` : ""}`;
    return { iana, offsetStr };
  } catch (e) {
    return { iana: "Asia/Ho_Chi_Minh", offsetStr: "UTC+7" };
  }
}

export function getTimezoneLabel(tzId) {
  const opt = TIMEZONE_OPTIONS.find((t) => t.id === tzId);
  if (!opt) return "Giờ Máy Tính";
  if (tzId === "local") {
    const sys = getSystemTimezoneInfo();
    return `Máy Tính (${sys.offsetStr})`;
  }
  return opt.label;
}

export function parseChartTimeToDate(time) {
  if (typeof time === "number") {
    return new Date(time * 1000);
  }
  if (time && typeof time === "object" && "year" in time) {
    return new Date(Date.UTC(time.year, time.month - 1, time.day));
  }
  if (typeof time === "string") {
    return new Date(time);
  }
  return new Date();
}

// Cached Intl.DateTimeFormat instances for performance
const formatterCache = new Map();

function getCachedFormatter(tzId, options, locale = "en-GB") {
  const key = `${tzId}_${locale}_${JSON.stringify(options)}`;
  if (!formatterCache.has(key)) {
    const opts = { ...options };
    if (tzId && tzId !== "local") {
      opts.timeZone = tzId;
    }
    formatterCache.set(key, new Intl.DateTimeFormat(locale, opts));
  }
  return formatterCache.get(key);
}

/**
 * Creates a timeFormatter function for Lightweight Charts localization options.
 * Overrides the crosshair floating tooltip on the horizontal time scale.
 */
export function createChartTimeFormatter(tzId = "local") {
  return (time) => {
    try {
      const date = parseChartTimeToDate(time);
      const fmt = getCachedFormatter(
        tzId,
        {
          hour12: false,
          day: "2-digit",
          month: "short",
          year: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
        },
        "en-GB"
      );
      return fmt.format(date).replace(",", "");
    } catch (e) {
      return String(time);
    }
  };
}

/**
 * Creates a tickMarkFormatter function for Lightweight Charts timeScale options.
 * Formats the tick marks along the bottom horizontal axis.
 *
 * TickMarkType enum:
 * 0 = Year
 * 1 = Month
 * 2 = DayOfMonth
 * 3 = Time
 * 4 = TimeWithSeconds
 */
export function createChartTickMarkFormatter(tzId = "local") {
  return (time, tickMarkType, locale = "en-GB") => {
    try {
      const date = parseChartTimeToDate(time);

      if (tickMarkType === 0) {
        // Year
        const fmt = getCachedFormatter(tzId, { year: "numeric" }, locale);
        return fmt.format(date);
      } else if (tickMarkType === 1) {
        // Month
        const fmt = getCachedFormatter(tzId, { month: "short" }, locale);
        return fmt.format(date);
      } else if (tickMarkType === 2) {
        // DayOfMonth
        const fmt = getCachedFormatter(tzId, { day: "2-digit", month: "short" }, locale);
        return fmt.format(date);
      } else if (tickMarkType === 3) {
        // Time
        const fmt = getCachedFormatter(tzId, { hour12: false, hour: "2-digit", minute: "2-digit" }, locale);
        return fmt.format(date);
      } else if (tickMarkType === 4) {
        // TimeWithSeconds
        const fmt = getCachedFormatter(
          tzId,
          { hour12: false, hour: "2-digit", minute: "2-digit", second: "2-digit" },
          locale
        );
        return fmt.format(date);
      }
    } catch (e) {}
    return null;
  };
}

/**
 * Formats a Unix timestamp (seconds) into standard time string for UI components.
 */
export function formatDisplayTime(timeSec, tzId = "local", formatType = "time") {
  if (!timeSec) return "--:--";
  try {
    const date = new Date(timeSec * 1000);
    if (formatType === "time") {
      const fmt = getCachedFormatter(tzId, { hour12: false, hour: "2-digit", minute: "2-digit" }, "vi-VN");
      return fmt.format(date);
    } else if (formatType === "full") {
      const fmt = getCachedFormatter(
        tzId,
        {
          hour12: false,
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        },
        "vi-VN"
      );
      return fmt.format(date);
    } else if (formatType === "date") {
      const fmt = getCachedFormatter(
        tzId,
        {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
        },
        "vi-VN"
      );
      return fmt.format(date);
    }
  } catch (e) {
    return String(timeSec);
  }
}
